import React, {useState, useEffect} from 'react';
import {Window, Button, TextInput, Fieldset} from 'react95';
import styles from '../styles/modules/Window.module.css';

import {t} from '../data/translations';
import {guestbookService, GuestbookMessage} from '../services/guestbookService';
import {authService} from '../services/authService';

interface WindowGuestbookProps {
  onClose: () => void;
}

const WindowGuestbook: React.FC<WindowGuestbookProps> = ({onClose}) => {
  const [messages, setMessages] = useState<GuestbookMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [newAuthor, setNewAuthor] = useState('');
  const [addingMessage, setAddingMessage] = useState(false);
  const [currentUser, setCurrentUser] = useState(authService.getCurrentUser());


  useEffect(() => {
    loadMessages();
    if (currentUser) {
      setNewAuthor(currentUser.name);
    }
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await guestbookService.getAllMessages();
      if (response.success) {
        setMessages(response.data as GuestbookMessage[]);
      } else {
        setError(response.error || 'Ошибка загрузки сообщений');
      }
    } catch (err) {
      setError('Ошибка загрузки сообщений');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !newAuthor.trim()) return;

    setAddingMessage(true);


    try {
      const response = await guestbookService.addMessage(newAuthor.trim(), newMessage.trim());

      if (response.success) {
        setNewMessage('');
        setShowAddForm(false);
        await loadMessages();

      } else {
        setError(response.error || 'Ошибка добавления сообщения');

      }
    } catch (err) {
      setError('Ошибка добавления сообщения');

    } finally {
      setAddingMessage(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const sortedMessages = [...messages].sort((a, b) => 
    new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
  );

  return (
    <Window className={`${styles.window} ${styles.guestbookWindow}`}>
      <div className={styles.windowHeader}>
        <span>{t('windows.guestbook.title')}</span>
        <Button onClick={onClose} size="sm">
          <span>✕</span>
        </Button>
      </div>

      <div className={styles.windowContent}>
        <div className={styles.guestbookContainer}>
          <h1 className={styles.guestbookTitle}>{t('windows.guestbook.mainTitle')}</h1>

          {error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.guestbookActions}>
            <Button onClick={() => setShowAddForm(!showAddForm)} className={styles.addButton}>
              {t('windows.guestbook.writeMessage')}
            </Button>
            <Button onClick={loadMessages} disabled={loading} className={styles.refreshButton}>
              🔄 Обновить
            </Button>
          </div>

          {showAddForm && (
            <Fieldset label="Написать сообщение" className={styles.addForm}>
              <form onSubmit={handleAddMessage}>
                <div className={styles.formField}>
                  <label htmlFor="author">{t('windows.guestbook.authorPlaceholder')}:</label>
                  <TextInput
                    id="author"
                    value={newAuthor}
                    onChange={e => setNewAuthor(e.target.value)}
                    placeholder={t('windows.guestbook.authorPlaceholder')}
                    required
                  />
                </div>
                <div className={styles.formField}>
                  <label htmlFor="message">{t('windows.guestbook.messagePlaceholder')}:</label>
                  <TextInput
                    id="message"
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    placeholder={t('windows.guestbook.messagePlaceholder')}
                    multiline
                    rows={4}
                    required
                  />
                </div>
                <div className={styles.formActions}>
                  <Button type="submit" disabled={addingMessage || !newMessage.trim() || !newAuthor.trim()}>
                    {addingMessage ? 'Отправка...' : t('windows.guestbook.submitMessage')}
                  </Button>
                  <Button onClick={() => setShowAddForm(false)} disabled={addingMessage}>
                    Отмена
                  </Button>
                </div>
              </form>
            </Fieldset>
          )}

          {loading ? (
            <div className={styles.loading}>Загрузка сообщений...</div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{t('windows.guestbook.noMessages')}</p>
              <p>Будьте первым, кто оставит пожелание! 🎉</p>
            </div>
          ) : (
            <div className={styles.messagesContainer}>
              <h2>{t('windows.guestbook.messages')}</h2>
              <div className={styles.messagesList}>
                {sortedMessages.map(msg => (
                  <div key={msg.id} className={styles.messageItem}>
                    <span className={styles.messageAuthor}>{msg.author}</span>
                    <span className={styles.messageDate}>{formatDate(msg.created_at || '')}</span>
                    <span className={styles.messageContent}>{msg.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Window>
  );
};

export default WindowGuestbook;
